import React from 'react';
import { AlertTriangle, Loader2, Lock, RefreshCw, ShieldCheck } from 'lucide-react';
import { UnlockButton } from './UnlockButton';
import { PaymentNotice } from '../../Payment/PaymentNotice';
import { reportUnlockFeatures } from '../../../data/reportUnlockFeatures';
import { PLAN_PRICES_USD, formatUsd } from '../../../data/pricingPlans';
import type { CheckoutState } from '../../../hooks/useReportUnlock';
export type PaymentReturnNotice = 'cancelled' | 'failed' | 'expired';
const NOTICE_COPY: Record<PaymentReturnNotice, string> = {
  cancelled: 'Checkout was cancelled, so this report is still locked. You can unlock it whenever you’re ready.',
  failed: 'The payment didn’t go through, so this report is still locked. You can try again below.',
  expired: 'The checkout session expired before payment, so this report is still locked. Start a new checkout below.'
};
interface ReportUnlockCardProps {
  patternCount: number;
  state: CheckoutState;
  error: string | null;
  notice: PaymentReturnNotice | null;
  onUnlock: () => void;
  onCheckAgain: () => void;
}
export function ReportUnlockCard({
  patternCount,
  state,
  error,
  notice,
  onUnlock,
  onCheckAgain
}: ReportUnlockCardProps) {
  const price = formatUsd(PLAN_PRICES_USD.single_scan);
  const patternsText = patternCount > 0 ? `ToxicDetector identified ${patternCount} communication ${patternCount === 1 ? 'pattern' : 'patterns'}. Unlock the complete report to see exactly what triggered each finding.` : 'Unlock the complete report for the full message-by-message breakdown of this conversation.';
  const actions = <CardActions price={price} state={state} error={error} notice={notice} onUnlock={onUnlock} onCheckAgain={onCheckAgain} data-id="element-359" data-mp="1ku4gw1xt8laq" />;
  return <section id="unlock-report" aria-labelledby="unlock-heading" className="scroll-mt-24 rounded-2xl border border-forensic-crimson/45 bg-forensic-card shadow-[0_24px_80px_-40px_rgba(181,20,47,0.55)]" data-id="element-360" data-mp="1tu9mc6jg19m9">
      <div className="grid gap-8 p-5 sm:p-10 lg:grid-cols-[1.15fr_1fr] lg:gap-14" data-id="element-361" data-mp="3uwscjzam1srs">
        <div data-id="element-362" data-mp="3qv6ulzj7yoaq">
          <div className="flex items-center gap-3" data-id="element-363" data-mp="3qvowpcskbwqk">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-forensic-crimson/35 bg-forensic-crimson/10" data-id="element-364" data-mp="8ged8oad6lh8">
              <Lock size={17} className="text-forensic-crimson-soft" aria-hidden="true" data-id="element-365" data-mp="3owb3wwlueuo" />
            </span>
            <div data-id="element-366" data-mp="id0wj8n1l4h5">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-forensic-subtle" data-id="element-367" data-mp="igormc84xyxz">
                Report Status
              </p>
              <p className="text-sm font-medium text-white" data-id="element-368" data-mp="rfijxqefo35q">Full report sealed</p>
            </div>
          </div>
          <h2 id="unlock-heading" className="mt-6 text-2xl font-semibold leading-tight tracking-tight text-white sm:text-[28px]" data-id="element-369" data-mp="mh8pxj4g38lp">
            Your complete forensic analysis is ready
          </h2>
          <p className="mt-3 text-base leading-relaxed text-gray-300" data-id="element-370" data-mp="2o9deckmqjnro">{patternsText}</p>

          <div className="mt-8 hidden lg:block" data-id="element-371" data-mp="13q1eo81ggj0v">{actions}</div>
        </div>

        <div className="lg:border-l lg:border-forensic-line lg:pl-14" data-id="element-372" data-mp="18njgzfkyvzzw">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-forensic-subtle" data-id="element-373" data-mp="uxps4x9gpfq6">
            Included in the full report
          </p>
          <ul className="mt-4 space-y-3" data-id="element-374" data-mp="ywylw24i5dlq">
            {reportUnlockFeatures.map(feature => <li key={feature} className="flex items-center gap-3 text-sm text-gray-200" data-id="element-375" data-mp="kxp4iz4k8dg6">
                <Lock size={13} className="shrink-0 text-forensic-subtle" aria-hidden="true" data-id="element-376" data-mp="pzo8aa47pcdn" />
                {feature}
              </li>)}
          </ul>
        </div>

        <div className="lg:hidden" data-id="element-377" data-mp="g0yp8e0kxlvt">{actions}</div>
      </div>
    </section>;
}
function CardActions({
  price,
  state,
  error,
  notice,
  onUnlock,
  onCheckAgain
}: {
  price: string;
  state: CheckoutState;
  error: string | null;
  notice: PaymentReturnNotice | null;
  onUnlock: () => void;
  onCheckAgain: () => void;
}) {
  return <div data-id="element-378" data-mp="1ll7s8qwxv2jf">
      <StatusNotice state={state} error={error} notice={notice} onCheckAgain={onCheckAgain} data-id="element-379" data-mp="1lkxzwwof8jak" />
      <PaymentNotice className="mb-4" data-id="element-380" data-mp="1gmd213wn6bpa" />
      <UnlockButton label={`Unlock Full Forensic Report — ${price}`} state={state} onClick={onUnlock} fullWidth data-id="element-381" data-mp="1bobhsfnhc6c8" />
      <p className="mt-4 text-center text-sm text-forensic-muted" data-id="element-382" data-mp="16s4aojjckosp">
        One-time payment · No subscription required
      </p>
      <p className="mt-1.5 flex items-center justify-center gap-1.5 text-xs text-forensic-subtle" data-id="element-383" data-mp="xxzoidf5s1ji">
        <ShieldCheck size={12} aria-hidden="true" data-id="element-384" data-mp="syd9byfrsb82" />
        Secure checkout · Your conversation remains private
      </p>
    </div>;
}
function StatusNotice({
  state,
  error,
  notice,
  onCheckAgain
}: {
  state: CheckoutState;
  error: string | null;
  notice: PaymentReturnNotice | null;
  onCheckAgain: () => void;
}) {
  if (state === 'confirming') {
    return <div role="status" aria-live="polite" className="mb-4 flex items-start gap-3 rounded-xl border border-forensic-line bg-forensic-surface px-4 py-3" data-id="element-385" data-mp="f17xfhtdki4w">
        <Loader2 size={16} className="mt-0.5 shrink-0 animate-spin text-forensic-muted" aria-hidden="true" data-id="element-386" data-mp="2ulzhmfi1c3xk" />
        <div data-id="element-387" data-mp="2zkswoccz02zu">
          <p className="text-sm font-medium text-white" data-id="element-388" data-mp="34lyma1bc3a55">Confirming payment…</p>
          <p className="mt-0.5 text-sm text-forensic-muted" data-id="element-389" data-mp="34jvylnxnlt3l">
            Your report unlocks as soon as payment is confirmed. Keep this page open.
          </p>
        </div>
      </div>;
  }
  if (state === 'timed_out') {
    return <div role="status" aria-live="polite" className="mb-4 rounded-xl border border-forensic-line bg-forensic-surface px-4 py-3" data-id="element-390" data-mp="2gvvwxsyt0ia8">
        <p className="text-sm font-medium text-white" data-id="element-391" data-mp="2lp75byj9l749">Still locked</p>
        <p className="mt-0.5 text-sm text-forensic-muted" data-id="element-392" data-mp="1o6xbalvh2y06">
          If you were charged, it will unlock shortly.
        </p>
        <button type="button" onClick={onCheckAgain} className="mt-2 inline-flex min-h-[36px] items-center gap-1.5 text-sm font-medium text-white underline-offset-4 transition-colors duration-150 ease-out hover:text-forensic-crimson-soft hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-forensic-crimson-soft" data-id="element-393" data-mp="1t4qini8ihy21">
          <RefreshCw size={14} aria-hidden="true" data-id="element-394" data-mp="1pxdn3isvhcyw" />
          Check again
        </button>
      </div>;
  }
  if (state === 'still_locked') {
    return <div role="status" className="mb-4 rounded-xl border border-forensic-line bg-forensic-surface px-4 py-3" data-id="element-395" data-mp="y665kuh75r9b">
        <p className="text-sm font-medium text-white" data-id="element-396" data-mp="1c5pbrhqg6siz">Still locked</p>
        <p className="mt-0.5 text-sm text-forensic-muted" data-id="element-397" data-mp="176ciof8zgp7y">
          {notice ? NOTICE_COPY[notice] : 'Checkout didn’t complete, so this report is still locked. You can try again below.'}
        </p>
      </div>;
  }
  if (state === 'error' && error) {
    return <p role="alert" className="mb-4 flex items-start gap-2 text-sm text-forensic-crimson-soft" data-id="element-398" data-mp="4dqekp58r0zk">
        <AlertTriangle size={15} className="mt-0.5 shrink-0" aria-hidden="true" data-id="element-399" data-mp="9ctl6e0vdf9n" />
        {error}
      </p>;
  }
  return null;
}